import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  getCompanyName,
  HStack,
  VStack,
} from "../../../components/utils";
import {
  CheckBoxWithLabel,
  Input,
  Label,
} from "../../../components/shared/InputField";
import { Select } from "../../../components/shared/Select";
import { MultiSelectV2 } from "../../../components/shared/MultiSelectV2";
import { ReportType, SingleReportMetadata } from "../utils/report";
import { OptionTypes } from "../../../components/shared/MultiSelect";
import { useEsopPlans } from "../../../queries";
import { PlanState } from "../../../types/EsopPlan";
import { generateGrantReport, getReportMetadata } from "../../../api/Esop";
import { ReportDto } from "../../../types/Grant";
import {
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../../utils/DownloadFile";
import { useGetOptionBuyback } from "../../../queries/optionBuyback";
import {
  EventType,
  MonetaryTransactionType,
} from "../../../types/OptionBuyback";
import { SwitchButton } from "../../../components/shared/SwitchButton";
import Tooltip from "../../../components/shared/Tooltip";
import { useReportJobStore } from "../../../store/reportJobStore";
import { useCompanyStore } from "../../../store";
import { HissaReportType, ScheduleParams } from "../../../types/common";
import ScheduleSection from "./ScheduleSection";
import { useGetAssociatedUsers } from "../../../queries/usersRoles";
import { useGetScheduledReports } from "../../../queries/scheduledReports";
import {
  getFinancialReportSheetColumns,
  getReportColumns,
  isColumnChooserSupported,
} from "../utils/reportColumns";
import {
  deleteReportPreset,
  getReportPresets,
  saveReportPreset,
} from "../../../api/reportPresets";
import { AvailableTableFilters, TableName } from "../../../types/Table";
import ColumnChooserSection from "./ColumnChooserSection";

type ResolvedEvent = {
  id: string;
  eventName: string;
  eventType: EventType | string;
};

const ReportDialog = ({
  onClose = () => {},
  data,
}: Readonly<{
  onClose: () => void;
  data: any;
}>) => {
  const { data: _plans } = useEsopPlans();
  const { data: _details } = useGetOptionBuyback();
  const { upsertReport } = useReportJobStore();
  const { companyData } = useCompanyStore();
  const { data: associatedUsers } = useGetAssociatedUsers();
  const { data: scheduledReports = [], refetch: refetchScheduledReports } =
    useGetScheduledReports();
  const existingSchedule =
    data?.reportType &&
    scheduledReports.find(
      (s) => s.reportType === data.reportType && s.isActive
    );
  const plans = _plans || [];
  const events = _details || [];
  const [metadata, setMetadata] = useState<SingleReportMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);

  const resolvedPlans = useMemo(() => {
    if (metadata?.plans?.length) {
      return metadata.plans.map((plan) => ({
        esopPlanId: plan.id,
        planName: plan.name,
        esopPlanState: plan.state,
      }));
    }
    return plans;
  }, [metadata?.plans, plans]);

  const resolvedEvents = useMemo<ResolvedEvent[]>(() => {
    if (metadata?.events?.length) {
      return metadata.events.map((event) => ({
        id: event.id || "",
        eventName: event.name,
        eventType: event.eventType,
      }));
    }
    return events.map((event) => ({
      id: event.id || "",
      eventName: event.eventName,
      eventType: event.eventType,
    }));
  }, [metadata?.events, events]);

  const resolvedGrantStates = useMemo(
    () =>
      metadata?.filters?.grantStates?.length
        ? metadata.filters.grantStates.map((value) => ({ value }))
        : [
            { value: "CREATED" },
            { value: "FOR_APPROVAL" },
            { value: "APPROVED" },
            { value: "OFFERED" },
            { value: "GRANTED" },
            { value: "WITHDRAW" },
            { value: "OFFER_EXPIRED" },
          ],
    [metadata?.filters?.grantStates]
  );

  const resolvedEmploymentStates = useMemo(
    () =>
      metadata?.filters?.employeeStates?.length
        ? metadata.filters.employeeStates.map((value) => ({ value }))
        : [{ value: "EMPLOYED" }, { value: "RESIGNED" }],
    [metadata?.filters?.employeeStates]
  );
  const planOptions: OptionTypes[] = [];
  const buyBackEvents: OptionTypes[] = [];
  const liquidityEvents: OptionTypes[] = [];
  const ids: string[] = [];
  const buyBackIds: string[] = [];
  const liquidityIds: string[] = [];
  resolvedPlans.forEach((plan) => {
    planOptions.push({ value: plan.planName });
    ids.push(plan.esopPlanId);
  });
  resolvedEvents.forEach((event) => {
    if (event.eventType === EventType.CASH) {
      buyBackEvents.push({ value: event.eventName });
      buyBackIds.push(event.id || "");
    } else if (event.eventType === EventType.LIQUIDITY) {
      liquidityEvents.push({ value: event.eventName });
      liquidityIds.push(event.id || "");
    }
  });

  const [loading, setLoading] = useState(false);
  const [scheduleParams, setScheduleParams] = useState<
    ScheduleParams | undefined
  >(undefined);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [customHeaders, setCustomHeaders] = useState<Record<string, string>>(
    {}
  );
  const [financialGrantSelectedColumns, setFinancialGrantSelectedColumns] =
    useState<string[]>([]);
  const [financialGrantCustomHeaders, setFinancialGrantCustomHeaders] =
    useState<Record<string, string>>({});
  const [financialVestingSelectedColumns, setFinancialVestingSelectedColumns] =
    useState<string[]>([]);
  const [financialVestingCustomHeaders, setFinancialVestingCustomHeaders] =
    useState<Record<string, string>>({});
  const [reportPresets, setReportPresets] = useState<AvailableTableFilters[]>(
    []
  );
  const [selectedPresetId, setSelectedPresetId] = useState<string>("");
  const [presetName, setPresetName] = useState<string>("");
  const [savePresetOnGenerate, setSavePresetOnGenerate] =
    useState<boolean>(false);
  const [chooserOrderSyncToken, setChooserOrderSyncToken] = useState(0);
  const [baselineColumnState, setBaselineColumnState] = useState<string>("");
  const [isPresetLoading, setIsPresetLoading] = useState(false);

  const dateTypes = [
    ...(data?.name !== "SEBI Report" ? ["As of Date"] : []),
    "Current Financial Year",
    "Previous Financial Year",
    "Custom Interval",
  ];
  const offerStates: OptionTypes[] = [
    { value: "DRAFT" },
    { value: "OFFERED" },
    { value: "EXPIRED" },
    { value: "ACCEPTED" },
    { value: "REJECTED" },
    { value: "PENDING_EXERCISED" },
    { value: "COMPLETED" },
  ];
  const [showAdvanceFilters, setShowAdvanceFilters] = useState(false);
  const [selectedDateType, setSelectedDateType] = useState("");
  const [shouldPiiBeMasked, setShouldPiiBeMasked] = useState(false);
  const isFinancialReport =
    data?.reportType === HissaReportType.FINANCIAL_REPORT;
  const localFinancialSheetColumns = useMemo(
    () => getFinancialReportSheetColumns(),
    []
  );
  const localReportColumns = useMemo(
    () => getReportColumns(data?.reportType),
    [data?.reportType]
  );
  const financialSheetColumns = useMemo(() => {
    const grantSheet = metadata?.columnChooser?.financialSheets?.grantSheet;
    const vestingSheet = metadata?.columnChooser?.financialSheets?.vestingSheet;
    if (grantSheet?.length && vestingSheet?.length) {
      return {
        grantSheet,
        vestingSheet,
      };
    }
    return localFinancialSheetColumns;
  }, [metadata?.columnChooser?.financialSheets, localFinancialSheetColumns]);

  const reportColumns = useMemo(() => {
    const metadataColumns = metadata?.columnChooser?.columns;
    if (metadataColumns?.length) {
      return metadataColumns;
    }
    return localReportColumns;
  }, [metadata?.columnChooser?.columns, localReportColumns]);

  const shouldShowColumnChooser = isFinancialReport
    ? isColumnChooserSupported(data?.reportType)
    : (metadata?.columnChooser?.supported ??
        isColumnChooserSupported(data?.reportType)) &&
      reportColumns.length > 0;
  const reportPresetTableName = useMemo<TableName | null>(() => {
    if (!shouldShowColumnChooser || !data?.reportType) {
      return null;
    }
    return `REPORT_COLUMNS_${data.reportType}`;
  }, [shouldShowColumnChooser, data?.reportType]);

  const getFilteredHeaders = (
    selected: string[],
    headers: Record<string, string>
  ) =>
    Object.fromEntries(
      Object.entries(headers).filter(
        ([key, value]) => selected.includes(key) && Boolean(value?.trim())
      )
    );

  const getComparableColumnState = () => {
    if (!shouldShowColumnChooser) return "";

    const state = isFinancialReport
      ? {
          columnSelection: {
            grantSheet: {
              selectedColumns: financialGrantSelectedColumns,
              customHeaders: getFilteredHeaders(
                financialGrantSelectedColumns,
                financialGrantCustomHeaders
              ),
            },
            vestingSheet: {
              selectedColumns: financialVestingSelectedColumns,
              customHeaders: getFilteredHeaders(
                financialVestingSelectedColumns,
                financialVestingCustomHeaders
              ),
            },
          },
        }
      : {
          selectedColumns,
          customHeaders: getFilteredHeaders(selectedColumns, customHeaders),
        };

    return JSON.stringify(state);
  };

  const hasColumnSelectionChanges =
    shouldShowColumnChooser &&
    Boolean(baselineColumnState) &&
    baselineColumnState !== getComparableColumnState();

  useEffect(() => {
    if (!shouldShowColumnChooser) {
      setSelectedColumns([]);
      setCustomHeaders({});
      setFinancialGrantSelectedColumns([]);
      setFinancialGrantCustomHeaders({});
      setFinancialVestingSelectedColumns([]);
      setFinancialVestingCustomHeaders({});
      setSavePresetOnGenerate(false);
      setPresetName("");
      setBaselineColumnState("");
      return;
    }
    if (isFinancialReport) {
      const defaultGrantSelected = financialSheetColumns.grantSheet.map(
        (c) => c.key
      );
      const defaultVestingSelected = financialSheetColumns.vestingSheet.map(
        (c) => c.key
      );

      setFinancialGrantSelectedColumns(defaultGrantSelected);
      setFinancialGrantCustomHeaders({});
      setFinancialVestingSelectedColumns(defaultVestingSelected);
      setFinancialVestingCustomHeaders({});
      setSavePresetOnGenerate(false);
      setPresetName("");
      setBaselineColumnState(
        JSON.stringify({
          columnSelection: {
            grantSheet: {
              selectedColumns: defaultGrantSelected,
              customHeaders: {},
            },
            vestingSheet: {
              selectedColumns: defaultVestingSelected,
              customHeaders: {},
            },
          },
        })
      );
      return;
    }
    const defaultSelected = reportColumns.map((c) => c.key);
    setSelectedColumns(defaultSelected);
    setCustomHeaders({});
    setSavePresetOnGenerate(false);
    setPresetName("");
    setBaselineColumnState(
      JSON.stringify({ selectedColumns: defaultSelected, customHeaders: {} })
    );
  }, [
    shouldShowColumnChooser,
    reportColumns,
    isFinancialReport,
    financialSheetColumns,
  ]);

  useEffect(() => {
    let mounted = true;

    const loadMetadata = async () => {
      if (!data?.reportType) return;
      setMetadataLoading(true);
      try {
        const response = await getReportMetadata({
          reportTypes: [data.reportType],
          includeEvents: true,
          includePlans: true,
          includePresets: true,
        });
        if (!mounted) return;
        const reportMetadata = response?.data?.metadata?.[data.reportType];
        setMetadata(reportMetadata || null);
        setReportPresets(
          (reportMetadata?.presets as AvailableTableFilters[]) || []
        );
      } catch (_error) {
        if (!mounted) return;
        setMetadata(null);
        setReportPresets([]);
      } finally {
        if (mounted) {
          setMetadataLoading(false);
          setIsPresetLoading(false);
        }
      }
    };

    loadMetadata();

    return () => {
      mounted = false;
    };
  }, [data?.reportType]);

  const applyPreset = (preset: AvailableTableFilters) => {
    const state = (preset.filterState || {}) as {
      selectedColumns?: string[];
      customHeaders?: Record<string, string>;
      columnSelection?: {
        grantSheet?: {
          selectedColumns?: string[];
          customHeaders?: Record<string, string>;
        };
        vestingSheet?: {
          selectedColumns?: string[];
          customHeaders?: Record<string, string>;
        };
      };
      columnLabelSnapshot?: Record<string, string>;
    };

    if (isFinancialReport) {
      const grantSelected =
        state.columnSelection?.grantSheet?.selectedColumns || [];
      const vestingSelected =
        state.columnSelection?.vestingSheet?.selectedColumns || [];

      const grantKeySet = new Set(
        financialSheetColumns.grantSheet.map((c) => c.key)
      );
      const vestingKeySet = new Set(
        financialSheetColumns.vestingSheet.map((c) => c.key)
      );

      const validGrant = grantSelected.filter((key) => grantKeySet.has(key));
      const validVesting = vestingSelected.filter((key) =>
        vestingKeySet.has(key)
      );

      if (!validGrant.length || !validVesting.length) {
        toast(
          "This financial preset has no matching columns for one or more sheets",
          {
            type: "error",
            autoClose: 2500,
          }
        );
        return;
      }

      setFinancialGrantSelectedColumns(validGrant);
      setFinancialGrantCustomHeaders(
        Object.fromEntries(
          Object.entries(
            state.columnSelection?.grantSheet?.customHeaders || {}
          ).filter(
            ([key, value]) =>
              validGrant.includes(key) && Boolean(`${value || ""}`.trim())
          )
        )
      );
      setFinancialVestingSelectedColumns(validVesting);
      setFinancialVestingCustomHeaders(
        Object.fromEntries(
          Object.entries(
            state.columnSelection?.vestingSheet?.customHeaders || {}
          ).filter(
            ([key, value]) =>
              validVesting.includes(key) && Boolean(`${value || ""}`.trim())
          )
        )
      );
      setSelectedPresetId(preset.id);
      setSavePresetOnGenerate(false);
      setPresetName("");
      setChooserOrderSyncToken((prev) => prev + 1);
      setBaselineColumnState(
        JSON.stringify({
          columnSelection: {
            grantSheet: {
              selectedColumns: validGrant,
              customHeaders: Object.fromEntries(
                Object.entries(
                  state.columnSelection?.grantSheet?.customHeaders || {}
                ).filter(
                  ([key, value]) =>
                    validGrant.includes(key) && Boolean(`${value || ""}`.trim())
                )
              ),
            },
            vestingSheet: {
              selectedColumns: validVesting,
              customHeaders: Object.fromEntries(
                Object.entries(
                  state.columnSelection?.vestingSheet?.customHeaders || {}
                ).filter(
                  ([key, value]) =>
                    validVesting.includes(key) &&
                    Boolean(`${value || ""}`.trim())
                )
              ),
            },
          },
        })
      );
      toast("Preset applied", { type: "success", autoClose: 1800 });
      return;
    }

    const presetSelectedColumns = Array.isArray(state.selectedColumns)
      ? state.selectedColumns
      : [];
    const availableColumnKeys = new Set(reportColumns.map((c) => c.key));

    const validSelectedColumns = presetSelectedColumns.filter((key) =>
      availableColumnKeys.has(key)
    );

    if (!validSelectedColumns.length) {
      toast("This preset has no matching columns for the current report", {
        type: "error",
        autoClose: 2500,
      });
      return;
    }

    const selectedSet = new Set(validSelectedColumns);
    const nextCustomHeaders = Object.fromEntries(
      Object.entries(state.customHeaders || {}).filter(
        ([key, value]) =>
          selectedSet.has(key) && Boolean(`${value || ""}`.trim())
      )
    );

    setSelectedColumns(validSelectedColumns);
    setCustomHeaders(nextCustomHeaders);
    setChooserOrderSyncToken((prev) => prev + 1);
    setSelectedPresetId(preset.id);
    setSavePresetOnGenerate(false);
    setPresetName("");
    setBaselineColumnState(
      JSON.stringify({
        selectedColumns: validSelectedColumns,
        customHeaders: nextCustomHeaders,
      })
    );

    const removedColumns = presetSelectedColumns.filter(
      (key) => !availableColumnKeys.has(key)
    );
    const currentLabelMap = Object.fromEntries(
      reportColumns.map((column) => [column.key, column.label])
    );
    const headerMismatches = Object.entries(
      state.columnLabelSnapshot || {}
    ).filter(
      ([key, label]) => currentLabelMap[key] && currentLabelMap[key] !== label
    );

    if (removedColumns.length || headerMismatches.length) {
      const parts: string[] = [];
      if (removedColumns.length) {
        parts.push(`${removedColumns.length} column(s) no longer available`);
      }
      if (headerMismatches.length) {
        parts.push(`${headerMismatches.length} header label(s) changed`);
      }
      toast(`Preset applied with updates: ${parts.join(" and ")}.`, {
        type: "warning",
        autoClose: 3500,
      });
      return;
    }

    toast("Preset applied", { type: "success", autoClose: 1800 });
  };

  const refreshPresets = async () => {
    if (!reportPresetTableName) return;
    const presets = await getReportPresets(reportPresetTableName);
    setReportPresets(presets || []);
  };

  const saveCurrentPreset = async (nameOverride?: string) => {
    const cleanName = (nameOverride ?? presetName).trim();
    if (!reportPresetTableName) return;
    if (!cleanName) {
      toast("Please enter preset name", { type: "error", autoClose: 1800 });
      return;
    }
    if (
      (!isFinancialReport && !selectedColumns.length) ||
      (isFinancialReport &&
        (!financialGrantSelectedColumns.length ||
          !financialVestingSelectedColumns.length))
    ) {
      toast("Please select at least one column", {
        type: "error",
        autoClose: 1800,
      });
      return;
    }
    const duplicate = reportPresets.some(
      (preset) => preset.name?.toLowerCase() === cleanName.toLowerCase()
    );
    if (duplicate) {
      toast("A preset with this name already exists", {
        type: "error",
        autoClose: 2200,
      });
      return;
    }

    try {
      setIsPresetLoading(true);
      const filterState = isFinancialReport
        ? {
            version: 1,
            columnSelection: {
              grantSheet: {
                selectedColumns: financialGrantSelectedColumns,
                customHeaders: financialGrantCustomHeaders,
              },
              vestingSheet: {
                selectedColumns: financialVestingSelectedColumns,
                customHeaders: financialVestingCustomHeaders,
              },
            },
            columnLabelSnapshot: {
              grantSheet: Object.fromEntries(
                financialSheetColumns.grantSheet.map((column) => [
                  column.key,
                  column.label,
                ])
              ),
              vestingSheet: Object.fromEntries(
                financialSheetColumns.vestingSheet.map((column) => [
                  column.key,
                  column.label,
                ])
              ),
            },
          }
        : {
            version: 1,
            selectedColumns,
            customHeaders,
            columnLabelSnapshot: Object.fromEntries(
              reportColumns.map((column) => [column.key, column.label])
            ),
          };
      await saveReportPreset({
        tableName: reportPresetTableName,
        filterName: cleanName,
        filterState,
      });
      await refreshPresets();
      setPresetName("");
      setSavePresetOnGenerate(false);
      setBaselineColumnState(getComparableColumnState());
      toast("Preset saved", { type: "success", autoClose: 1800 });
    } catch (_error) {
      toast("Unable to save preset", { type: "error", autoClose: 2200 });
      throw _error;
    } finally {
      setIsPresetLoading(false);
    }
  };

  const deleteSelectedPreset = async () => {
    if (!selectedPresetId) {
      toast("Please select a preset to delete", {
        type: "error",
        autoClose: 1800,
      });
      return;
    }

    try {
      setIsPresetLoading(true);
      await deleteReportPreset(selectedPresetId);
      await refreshPresets();
      setSelectedPresetId("");
      toast("Preset deleted", { type: "success", autoClose: 1800 });
    } catch (_error) {
      toast("Unable to delete preset", { type: "error", autoClose: 2200 });
    } finally {
      setIsPresetLoading(false);
    }
  };

  const initialValues: ReportType = {
    grantStates: [],
    planIds: [],
    employeeStates: [],
    previewReport: false,
    grantIds: [],
    startDate: undefined,
    endDate: undefined,
    offerStates: [],
    eventIds: [],
    eventType: [],
    vestingCutOffDate: undefined,
  };
  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      setLoading(true);

      if (data?.name === "Liquidity Report") {
        values.eventType = [
          MonetaryTransactionType.EXERCISE_LIQUIDITY,
          MonetaryTransactionType.LIQUIDATION,
        ];
      }
      values.maskPiiData = shouldPiiBeMasked;
      if (data?.name === "SEBI Report") {
        if (!values.startDate || !values.endDate) {
          toast("Please select From date and To date", {
            type: "error",
            autoClose: 2000,
          });
          setLoading(false);
          return;
        }
      }
      if (data?.name === "BuyBack Report") {
        values.eventType = [MonetaryTransactionType.BUYBACK];
      }
      if (
        shouldShowColumnChooser &&
        isFinancialReport &&
        (financialGrantSelectedColumns.length === 0 ||
          financialVestingSelectedColumns.length === 0)
      ) {
        toast("Please select at least one column in each Financial sheet", {
          type: "error",
          autoClose: 2000,
        });
        setLoading(false);
        return;
      }
      if (
        shouldShowColumnChooser &&
        !isFinancialReport &&
        selectedColumns.length === 0
      ) {
        toast("Please select at least one column", {
          type: "error",
          autoClose: 2000,
        });
        setLoading(false);
        return;
      }
      if (shouldShowColumnChooser && isFinancialReport) {
        values.columnSelection = {
          grantSheet: {
            selectedColumns: financialGrantSelectedColumns,
            customHeaders: Object.fromEntries(
              Object.entries(financialGrantCustomHeaders).filter(
                ([key, value]) =>
                  financialGrantSelectedColumns.includes(key) &&
                  Boolean(value?.trim())
              )
            ),
          },
          vestingSheet: {
            selectedColumns: financialVestingSelectedColumns,
            customHeaders: Object.fromEntries(
              Object.entries(financialVestingCustomHeaders).filter(
                ([key, value]) =>
                  financialVestingSelectedColumns.includes(key) &&
                  Boolean(value?.trim())
              )
            ),
          },
        };
      } else if (shouldShowColumnChooser) {
        values.selectedColumns = selectedColumns;
        values.customHeaders = Object.fromEntries(
          Object.entries(customHeaders).filter(
            ([key, value]) =>
              selectedColumns.includes(key) && Boolean(value?.trim())
          )
        );
      }
      if (values.startDate === "") {
        values.startDate = undefined;
      }
      if (values.endDate === "") {
        values.endDate = undefined;
      }
      if (values.vestingCutOffDate === "") {
        values.vestingCutOffDate = undefined;
      }

      if (savePresetOnGenerate && hasColumnSelectionChanges) {
        if (!presetName.trim()) {
          toast("Preset name is mandatory when save preset is checked", {
            type: "error",
            autoClose: 1800,
          });
          setLoading(false);
          return;
        }
        try {
          await saveCurrentPreset(presetName.trim());
        } catch (_error) {
          setLoading(false);
          return;
        }
      }

      try {
        const payload =
          scheduleParams && scheduleParams.recipients.length > 0
            ? { ...values, scheduleParams }
            : values;
        await data?.onSubmitFunction(payload);
        if (scheduleParams && scheduleParams.recipients.length > 0) {
          refetchScheduledReports();
        }
        if (companyData)
          toast(
            scheduleParams && scheduleParams.recipients.length > 0
              ? "Report scheduled successfully. Recipients will receive an email with the report."
              : "Your report has been queued. Come back to this page and click Download once it's ready.",
            { type: "info", autoClose: 3000 }
          );

        onClose();
      } catch (err: any) {
        toast(
          err?.response?.data?.errorMessage ||
            err?.response?.data?.errors ||
            "Error while downloading the report",
          { type: "error", autoClose: 2000 }
        );
      } finally {
        setLoading(false);
      }
    },
  });
  const fileName = `${data?.name}_${getCompanyName()}.xlsx`;
  async function downloadReport(reportDto: ReportDto) {
    onClose();
    const blob = await convertBase64ToBlob(
      reportDto.base64File,
      reportDto.fileType
    );
    downloadBlobObject(blob, fileName);
  }
  const handleDateTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.target.value) {
      setSelectedDateType(e.target.value);
      const date = new Date();
      switch (e.target.value) {
        case "As of Date":
          formik.setFieldValue("startDate", "");
          break;
        case "Current Financial Year":
          formik.setFieldValue("startDate", `${date.getFullYear()}-04-01`);
          formik.setFieldValue("endDate", `${date.getFullYear() + 1}-03-31`);
          break;
        case "Previous Financial Year":
          formik.setFieldValue("startDate", `${date.getFullYear() - 1}-04-01`);
          formik.setFieldValue("endDate", `${date.getFullYear()}-03-31`);
          break;
        default:
          break;
      }
    }
  };

  return (
    <div className="">
      <div className="p-8 text-lg font-medium border-b">
        <h6 className="flex justify-between">
          {data?.headerName}
          <span
            className="cursor-pointer"
            onClick={() => {
              onClose();
            }}
          >
            X
          </span>
        </h6>
      </div>
      <VStack className="w-full gap-2 px-8 py-4 ">
        {existingSchedule && (
          <div className="flex items-start gap-2 bg-orange-50 border border-orange-200 rounded-md px-4 py-3 text-sm text-orange-800">
            <Icon
              icon="mdi:clock-alert-outline"
              width={16}
              className="mt-0.5 shrink-0 text-orange-500"
            />
            <span>
              This report is already scheduled (runs every{" "}
              <strong>{existingSchedule.intervalDays}</strong> day
              {existingSchedule.intervalDays !== 1 ? "s" : ""}). Submitting
              again will create an additional schedule.
            </span>
          </div>
        )}
        <HStack className="gap-8 ">
          <div className="flex-1">
            <Label className="text-sm font-normal">Date Type</Label>
            <Select
              options={dateTypes}
              onChange={(e) => handleDateTypeChange(e)}
              value={selectedDateType}
            />
          </div>
          {selectedDateType !== "As of Date" && (
            <div className="flex-1">
              <Label
                className={`text-sm font-normal ${
                  selectedDateType === "As of Date" ? "cursor-not-allowed" : ""
                }`}
              >
                {data?.startDate}
              </Label>
              <Input
                className={` ${
                  selectedDateType === "As of Date" ? "cursor-not-allowed" : ""
                }`}
                type="date"
                value={formik.values.startDate}
                onBlur={formik.handleBlur}
                disabled={selectedDateType === "As of Date"}
                onChange={(e) => {
                  if (!e.target.value) return;
                  formik.setFieldValue("startDate", e.target.value);
                }}
              />
            </div>
          )}
          <div className="flex-1">
            <Label className={"text-sm font-normal"}>{data?.endDate}</Label>
            <Input
              type="date"
              value={formik.values.endDate}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                if (!e.target.value) return;
                formik.setFieldValue("endDate", e.target.value);
              }}
            />
          </div>
          {selectedDateType === "As of Date" && <div className="flex-1"></div>}
        </HStack>
        <HStack className="pt-2 items-center gap-2 before:border-b-2 before:border-dashed before:m-auto after:flex-1 after:border-dashed after:content-[''] after:border-b-2">
          <ButtonPrimary1
            onClick={() => {
              if (showAdvanceFilters) {
                formik.resetForm();
              }
              setShowAdvanceFilters(!showAdvanceFilters);
            }}
            className="flex flex-row bg-transparent border border-solid cursor-pointer border-orange-501 hover:bg-transparent"
          >
            Advance Filters
            {showAdvanceFilters ? (
              <Icon
                icon="material-symbols:keyboard-arrow-down-rounded "
                className=""
              />
            ) : (
              <Icon
                icon="material-symbols:keyboard-arrow-down-rounded "
                className=""
              />
            )}
          </ButtonPrimary1>
        </HStack>
        {showAdvanceFilters ? (
          <VStack className="w-full gap-2 py-4">
            {data?.name !== "Liquidity Report" &&
            data?.name !== "BuyBack Report" ? (
              <HStack className="gap-8">
                <div className="flex-1">
                  <Label className={"text-sm font-normal"}>Grant states</Label>
                  <MultiSelectV2
                    _className="w-full"
                    multiple={true}
                    placeholder={"--Select states--"}
                    options={resolvedGrantStates}
                    handleSelection={(e) => {
                      formik.setFieldValue("grantStates", e);
                    }}
                    optionValues={formik.values.grantStates || []}
                  />
                </div>

                <div className="flex-1">
                  <Label className={"text-sm font-normal"}>
                    Employment Status
                  </Label>
                  <MultiSelectV2
                    _className="w-full"
                    multiple={true}
                    placeholder={"--Select status--"}
                    options={resolvedEmploymentStates}
                    handleSelection={(e) => {
                      formik.setFieldValue("employeeStates", e);
                    }}
                    optionValues={formik.values.employeeStates || []}
                  />
                </div>
              </HStack>
            ) : (
              <HStack className="gap-8">
                <div className="flex-1">
                  <Label className={"text-sm font-normal"}>Offer states</Label>
                  <MultiSelectV2
                    _className="w-full"
                    multiple={true}
                    placeholder={"--Select states--"}
                    options={offerStates}
                    handleSelection={(e) => {
                      formik.setFieldValue("offerStates", e);
                    }}
                    optionValues={formik.values.offerStates || []}
                  />
                </div>
                <div className="flex-1">
                  <Label className={"text-sm font-normal"}>Event Names</Label>

                  <MultiSelectV2
                    _className="w-full"
                    multiple={true}
                    placeholder={"--Select events--"}
                    options={
                      data?.name === "Liquidity Report"
                        ? liquidityEvents
                        : buyBackEvents
                    }
                    handleSelection={(e) => {
                      const eventIds = resolvedEvents
                        .filter((ev: ResolvedEvent) => e.includes(ev.eventName))
                        .map((ev: ResolvedEvent) => ev.id);
                      formik.setFieldValue("eventIds", eventIds);
                    }}
                    optionValues={resolvedEvents
                      .filter((p: ResolvedEvent) =>
                        formik.values.eventIds?.includes(p.id || "")
                      )
                      .map((p: ResolvedEvent) => p.eventName)}
                  />
                </div>
              </HStack>
            )}
            <HStack className="gap-8">
              <div className="flex-1">
                <Label className={"text-sm font-normal"}>Plan Names</Label>

                <MultiSelectV2
                  _className="w-full"
                  multiple={true}
                  placeholder={"--Select plans--"}
                  options={planOptions}
                  handleSelection={(e) => {
                    const planIds = resolvedPlans
                      .filter((p) => e.includes(p.planName))
                      .map((p) => p.esopPlanId);
                    formik.setFieldValue("planIds", planIds);
                  }}
                  optionValues={resolvedPlans
                    .filter((p) =>
                      formik.values.planIds?.includes(p.esopPlanId)
                    )
                    .map((p) => p.planName)}
                />
              </div>
              {(data?.name === "Financial Report" ||
                data?.name === "Grant Report") && (
                <div className="flex-1">
                  <Label className={"text-sm font-normal"}>
                    Transaction CutOff Date
                  </Label>
                  <Input
                    type="date"
                    value={formik.values.vestingCutOffDate}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      formik.setFieldValue("vestingCutOffDate", e.target.value);
                    }}
                  />
                </div>
              )}
            </HStack>
            {data?.name === "Financial Report" && (
              <HStack className="gap-8">
                <div className="flex-1">
                  <Box className="flex flex-row-reverse items-center justify-between">
                    <SwitchButton
                      className="mt-4 ml-4 mr-4"
                      value={formik.values.showFutureVesting || false}
                      label="Show Future vestings as zero"
                      onClick={() => {
                        formik.setFieldValue(
                          "showFutureVesting",
                          !formik.values.showFutureVesting
                        );
                      }}
                    />
                  </Box>
                </div>
                <div className="flex-1">
                  <Box className="flex flex-row-reverse items-center justify-between">
                    <SwitchButton
                      className="mt-4 ml-4 mr-4"
                      value={formik.values.considerVestedForOffered || false}
                      label="Consider Offered Grants as Vested"
                      onClick={() => {
                        formik.setFieldValue(
                          "considerVestedForOffered",
                          !formik.values.considerVestedForOffered
                        );
                      }}
                    />
                  </Box>
                </div>
              </HStack>
            )}
            {data?.name === "Financial Report" && formik.values.endDate && (
              <HStack className="gap-8 pt-4">
                <div className="flex-1">
                  <Box className="flex flex-row-reverse items-center justify-between">
                    <SwitchButton
                      className="mt-4 ml-4 mr-4"
                      value={
                        formik.values.changeEmploymentStatusAsPerCutoffDate ||
                        false
                      }
                      label="Employment Status as on End Date"
                      onClick={() => {
                        formik.setFieldValue(
                          "changeEmploymentStatusAsPerCutoffDate",
                          !formik.values.changeEmploymentStatusAsPerCutoffDate
                        );
                      }}
                      infoText={
                        "Toggling this will display the actual employment status in the report as of the grant cutoff date."
                      }
                    />
                  </Box>
                </div>
                <div className="flex-1"></div>
              </HStack>
            )}
            {shouldShowColumnChooser && (
              <HStack className="w-full gap-2 pt-2">
                <div className="flex-1">
                  <Label className="text-sm font-medium">
                    Column Chooser Presets
                  </Label>
                  <HStack className="gap-2 items-center">
                    <Select
                      className="flex-1 min-w-0"
                      options={reportPresets}
                      value={selectedPresetId}
                      placeholder={
                        isPresetLoading || metadataLoading
                          ? "Loading presets..."
                          : "--Select a saved preset--"
                      }
                      valueGetter={(preset) => preset.id}
                      textGetter={(preset) => preset.name || "Untitled Preset"}
                      onChange={(e) => {
                        const preset = reportPresets.find(
                          (entry) => entry.id === e.target.value
                        );
                        if (preset) {
                          applyPreset(preset);
                        } else {
                          setSelectedPresetId("");
                        }
                      }}
                    />
                  </HStack>
                </div>
                <div className="flex-1"></div>
              </HStack>
            )}
            {shouldShowColumnChooser &&
              (isFinancialReport ? (
                <VStack className="w-full gap-4">
                  <VStack className="w-full gap-2">
                    <Label className="text-sm font-medium">
                      Grant Level Sheet Columns
                    </Label>
                    <ColumnChooserSection
                      columns={financialSheetColumns.grantSheet}
                      selectedColumns={financialGrantSelectedColumns}
                      customHeaders={financialGrantCustomHeaders}
                      onSelectedColumnsChange={setFinancialGrantSelectedColumns}
                      onCustomHeadersChange={setFinancialGrantCustomHeaders}
                      sortable
                      orderSyncToken={chooserOrderSyncToken}
                    />
                  </VStack>
                  <VStack className="w-full gap-2">
                    <Label className="text-sm font-medium">
                      Vesting Level Sheet Columns
                    </Label>
                    <ColumnChooserSection
                      columns={financialSheetColumns.vestingSheet}
                      selectedColumns={financialVestingSelectedColumns}
                      customHeaders={financialVestingCustomHeaders}
                      onSelectedColumnsChange={
                        setFinancialVestingSelectedColumns
                      }
                      onCustomHeadersChange={setFinancialVestingCustomHeaders}
                      sortable
                      orderSyncToken={chooserOrderSyncToken}
                    />
                  </VStack>
                </VStack>
              ) : (
                <ColumnChooserSection
                  columns={reportColumns}
                  selectedColumns={selectedColumns}
                  customHeaders={customHeaders}
                  onSelectedColumnsChange={setSelectedColumns}
                  onCustomHeadersChange={setCustomHeaders}
                  sortable
                  orderSyncToken={chooserOrderSyncToken}
                  footerContent={
                    hasColumnSelectionChanges ? (
                      <VStack className="gap-2 mt-1">
                        <HStack className="items-center gap-2">
                          <input
                            type="checkbox"
                            checked={savePresetOnGenerate}
                            onChange={(e) => {
                              setSavePresetOnGenerate(e.target.checked);
                              if (!e.target.checked) {
                                setPresetName("");
                              }
                            }}
                            className="accent-orange-501"
                          />
                          <span className="text-xs text-gray-700">
                            Save this preset
                          </span>
                        </HStack>
                        {savePresetOnGenerate && (
                          <div className="w-full max-w-sm">
                            <Input
                              type="text"
                              value={presetName}
                              onChange={(e) => setPresetName(e.target.value)}
                              placeholder="Preset name"
                              className="w-full"
                            />
                            <div className="text-xxs text-gray-500 mt-1">
                              Preset name is mandatory when save is checked.
                            </div>
                          </div>
                        )}
                      </VStack>
                    ) : null
                  }
                />
              ))}

            {shouldShowColumnChooser &&
              isFinancialReport &&
              hasColumnSelectionChanges && (
                <VStack className="gap-2 mt-1">
                  <HStack className="items-center gap-2">
                    <input
                      type="checkbox"
                      checked={savePresetOnGenerate}
                      onChange={(e) => {
                        setSavePresetOnGenerate(e.target.checked);
                        if (!e.target.checked) {
                          setPresetName("");
                        }
                      }}
                      className="accent-orange-501"
                    />
                    <span className="text-xs text-gray-700">
                      Save this preset
                    </span>
                  </HStack>
                  {savePresetOnGenerate && (
                    <div className="w-full max-w-sm">
                      <Input
                        type="text"
                        value={presetName}
                        onChange={(e) => setPresetName(e.target.value)}
                        placeholder="Preset name"
                        className="w-full"
                      />
                      <div className="text-xxs text-gray-500 mt-1">
                        Preset name is mandatory when save is checked.
                      </div>
                    </div>
                  )}
                </VStack>
              )}
          </VStack>
        ) : (
          <></>
        )}
        {/* Schedule section */}

        <div className="mt-4 pt-4 border-t border-gray-100">
          <ScheduleSection
            value={scheduleParams}
            onChange={setScheduleParams}
            users={associatedUsers ?? []}
          />
        </div>

        <HStack className="justify-end mt-4 gap-2">
          <input
            type="checkbox"
            checked={shouldPiiBeMasked}
            onChange={() => setShouldPiiBeMasked(!shouldPiiBeMasked)}
            className="text-white accent-orange-501"
            name=""
            id=""
          />
          <span>Hide all Personally Identifiable Information</span>
        </HStack>
        <HStack className="justify-end m-3">
          <p className="italic">Note: The date filters are inclusive </p>
        </HStack>
        <HStack className="justify-end">
          <ButtonPrimary
            onClick={() => {
              if (scheduleParams && scheduleParams.recipients.length === 0) {
                toast(
                  "Please select at least one recipient to schedule the report",
                  { type: "error", autoClose: 2000 }
                );
                return;
              }
              formik.handleSubmit();
              refetchScheduledReports();
            }}
          >
            {!loading ? (
              scheduleParams ? (
                <span className="flex items-center gap-1.5">
                  <Icon icon="mdi:calendar-clock" width={16} />
                  Schedule
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <Icon icon="mdi:cog-play-outline" width={16} />
                  Queue Report
                </span>
              )
            ) : (
              <span className="flex items-center gap-1.5">
                <Icon
                  className="animate-spin"
                  icon="lucide:loader-2"
                  width={16}
                />
                {scheduleParams ? "Scheduling..." : "Generating..."}
              </span>
            )}
          </ButtonPrimary>
        </HStack>
      </VStack>
    </div>
  );
};

export default ReportDialog;
